import React, { useEffect, useState } from "react";
import { SectionToolbar, AddButton } from "./ProfileUI";
import { useParams } from "react-router-dom";
import {
  useEducation,
  useSaveEducation,
  useUploadEducationFile,
  useDeleteEducation,
} from "../../../api/queries/profileQueries";
import {
  useEmployee,
  useCheckCompleteness,
} from "../../../api/queries/employeeQueries";
import { getApiErrorMessage } from "../../../utils/apiError";

import "bootstrap-icons/font/bootstrap-icons.css";

// Stable empty list: `list` is an effect dependency below.
const EMPTY = [];

export const EducationLevel = {
  ELEMENTARY: "Elementary",
  JUNIORHIGH: "Junior High School",
  SENIORHIGH: "Senior High School",
  VOCATIONAL: "Vocational Course",
  COLLEGE: "College",
  POSTGRAD: "Post Graduate",
  OTHER: "Other",
};

const levelOrder = [
  EducationLevel.ELEMENTARY,
  EducationLevel.JUNIORHIGH,
  EducationLevel.SENIORHIGH,
  EducationLevel.VOCATIONAL,
  EducationLevel.COLLEGE,
  EducationLevel.POSTGRAD,
  EducationLevel.OTHER,
];

const EmployeeEducationBackgroundTable = () => {
  const { id: employeeId } = useParams();
  const { data: list = EMPTY } = useEducation(employeeId);
  // Needed for the upload's companyId (shared from the query cache)
  const { data: employee } = useEmployee(employeeId);
  const saveEducation = useSaveEducation(employeeId);
  const uploadEducationFile = useUploadEducationFile(employeeId);
  const deleteEducation = useDeleteEducation(employeeId);
  const checkCompleteness = useCheckCompleteness();

  const [editableList, setEditableList] = useState([]);
  const [newLevel, setNewLevel] = useState(EducationLevel.ELEMENTARY);
  const [statusMsg, setStatusMsg] = useState({ text: "", type: "" });
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    setEditableList(
      list
        .map((e) => ({ ...e, editing: false }))
        .sort(
          (a, b) => levelOrder.indexOf(a.level) - levelOrder.indexOf(b.level),
        ),
    );
  }, [list]);

  const showStatus = (text, type = "success") => {
    setStatusMsg({ text, type });
    setTimeout(() => setStatusMsg({ text: "", type: "" }), 4000);
  };

  const toggleEdit = (index) =>
    setEditableList((prev) =>
      prev.map((e, i) => (i === index ? { ...e, editing: !e.editing } : e)),
    );

  const handleChange = (index, field, value) =>
    setEditableList((prev) =>
      prev.map((e, i) => (i === index ? { ...e, [field]: value } : e)),
    );

  const handleCancel = (index, row) => {
    if (!row.id) {
      setEditableList((prev) => prev.filter((_, i) => i !== index));
    } else {
      const original = list.find((e) => e.id === row.id);
      setEditableList((prev) =>
        prev.map((e, i) => (i === index ? { ...original, editing: false } : e)),
      );
    }
  };

  const handleSave = async (entry, index) => {
    if (!entry.schoolName?.trim()) {
      showStatus("School name is required.", "danger");
      return;
    }

    const companyId = employee?.companyId || employee?.company?.id;
    setIsProcessing(true);

    try {
      const payload = {
        employeeId,
        level: entry.level,
        schoolName: entry.schoolName,
        schoolAddress: entry.schoolAddress || null,
        yearFrom: Number(entry.yearFrom),
        yearTo: Number(entry.yearTo),
        course: entry.course || null,
      };

      const saved = await saveEducation.mutateAsync({
        id: entry.id,
        data: payload,
      });
      const savedId = entry.id || saved.id;

      if (entry.file && typeof entry.file !== "string") {
        await uploadEducationFile.mutateAsync({
          id: savedId,
          file: entry.file,
          companyId,
        });
      }

      showStatus("Saved successfully!");
      // Recalculates Complete/Incomplete; the list and employee refresh automatically
      await checkCompleteness.mutateAsync(employeeId);
    } catch (err) {
      showStatus(getApiErrorMessage(err, "Failed to save."), "danger");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async (entry, index) => {
    if (!entry.id) {
      setEditableList((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    if (!window.confirm("Delete record?")) return;

    setIsProcessing(true);
    try {
      await deleteEducation.mutateAsync(entry.id);
      await checkCompleteness.mutateAsync(employeeId);
      showStatus("Deleted successfully!");
    } catch (err) {
      showStatus(getApiErrorMessage(err, "Failed to delete."), "danger");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddRow = () => {
    // Unique key generation for the new row to prevent cloning issues
    setEditableList((prev) => [
      ...prev,
      {
        id: null,
        level: newLevel,
        schoolName: "",
        schoolAddress: "",
        yearFrom: "",
        yearTo: "",
        course: "",
        editing: true,
        file: null,
      },
    ]);
  };

  return (
    <div className="position-relative">
      <div>
        {isProcessing && (
          <div className="progress-overlay">
            <div className="spinner-border text-primary"></div>
          </div>
        )}

        <SectionToolbar title="Schools" icon="bi-book" statusMsg={statusMsg}>
          <select
            className="form-select form-select-sm rounded-8 h-30px fs-075 w-auto"
            aria-label="Education level to add"
            value={newLevel}
            onChange={(e) => setNewLevel(e.target.value)}
          >
            {levelOrder.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
          <AddButton onClick={handleAddRow} label="Add school" />
        </SectionToolbar>

        <div className="table-responsive profile-table-wrap">
          <table className="table table-hover align-middle mb-0">
            <thead className="custom-table-header">
              <tr>
                <th className="py-3 ps-4">LEVEL</th>
                <th className="py-3">SCHOOL NAME</th>
                <th className="py-3 text-center col-w-80">FROM</th>
                <th className="py-3 text-center col-w-80">TO</th>
                <th className="py-3">COURSE</th>
                <th className="py-3 text-center">ATTACHMENT</th>
                <th className="py-3 pe-4 text-end col-w-130">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {editableList.map((row, index) => {
                const hasFile = row.file || row.file_url;

                return (
                  <tr
                    key={row.id || `edu-${index}-${row.level}`}
                    className={row.editing ? "bg-primary bg-opacity-10" : ""}
                  >
                    <td className="ps-4 fw-bold text-primary text-uppercase fs-070">
                      {row.level}
                    </td>
                    <td>
                      <input
                        type="text"
                        className={`form-control ${!row.editing ? "bg-transparent border-0" : ""} ${row.editing ? "table-field" : "fw-semibold"}`}
                        value={row.schoolName}
                        disabled={!row.editing}
                        onChange={(e) =>
                          handleChange(index, "schoolName", e.target.value)
                        }
                      />
                      {row.editing && (
                        <input
                          type="text"
                          className="form-control mt-1 table-field table-field--sm"
                          value={row.schoolAddress || ""}
                          placeholder="Address"
                          onChange={(e) =>
                            handleChange(index, "schoolAddress", e.target.value)
                          }
                        />
                      )}
                    </td>
                    <td className="text-center">
                      <input
                        type="text"
                        className={`form-control text-center ${!row.editing ? "bg-transparent border-0" : ""} ${row.editing ? "table-field" : ""}`}
                        value={row.yearFrom || ""}
                        disabled={!row.editing}
                        onChange={(e) =>
                          handleChange(
                            index,
                            "yearFrom",
                            e.target.value.replace(/\D/g, ""),
                          )
                        }
                      />
                    </td>
                    <td className="text-center">
                      <input
                        type="text"
                        className={`form-control text-center ${!row.editing ? "bg-transparent border-0" : ""} ${row.editing ? "table-field" : ""}`}
                        value={row.yearTo || ""}
                        disabled={!row.editing}
                        onChange={(e) =>
                          handleChange(
                            index,
                            "yearTo",
                            e.target.value.replace(/\D/g, ""),
                          )
                        }
                      />
                    </td>
                    <td>
                      {row.level === EducationLevel.COLLEGE ||
                      row.level === EducationLevel.OTHER ? (
                        <input
                          type="text"
                          className={`form-control ${!row.editing ? "bg-transparent border-0" : ""} ${row.editing ? "table-field" : ""}`}
                          value={row.course || ""}
                          disabled={!row.editing}
                          onChange={(e) =>
                            handleChange(index, "course", e.target.value)
                          }
                        />
                      ) : (
                        <span className="text-muted small px-2">—</span>
                      )}
                    </td>
                    <td className="text-center">
                      <div className="position-relative d-inline-block">
                        {row.editing && (
                          <input
                            type="file"
                            className="position-absolute opacity-0 w-100 h-100 cursor-pointer z-2 top-0 start-0"
                            onChange={(e) => {
                              handleChange(index, "file", e.target.files[0]);
                              e.target.value = "";
                            }}
                          />
                        )}
                        <div
                          className={`d-flex align-items-center justify-content-center px-2 py-1 fw-bold text-white shadow-sm attach-chip ${hasFile ? "is-done" : ""}`}
                        >
                          <i
                            className={`bi ${hasFile ? "bi-check-circle-fill" : "bi-cloud-arrow-up-fill"} me-2`}
                          ></i>
                          {hasFile ? "UPLOADED" : "ATTACH"}
                        </div>
                      </div>
                    </td>
                    <td className="pe-4 text-end">
                      <div className="d-flex justify-content-end gap-2">
                        {row.editing ? (
                          <>
                            <button
                              title="Save"
                              aria-label="Save"
                              className="btn btn-success btn-sm p-0 d-flex align-items-center justify-content-center btn-icon-28"
                              onClick={() => handleSave(row, index)}
                            >
                              <i className="bi bi-check-lg"></i>
                            </button>
                            <button
                              title="Cancel"
                              aria-label="Cancel"
                              className="btn btn-outline-secondary btn-sm p-0 d-flex align-items-center justify-content-center btn-icon-28"
                              onClick={() => handleCancel(index, row)}
                            >
                              <i className="bi bi-x-lg"></i>
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              title="Edit"
                              aria-label="Edit"
                              className="btn btn-sm btn-outline-primary p-0 d-flex align-items-center justify-content-center btn-icon-28"
                              onClick={() => toggleEdit(index)}
                            >
                              <i className="bi bi-pencil-square"></i>
                            </button>
                            {row.file_url && (
                              <a
                                title="View file"
                                aria-label="View file"
                                href={row.file_url}
                                target="_blank"
                                rel="noreferrer"
                                className="btn btn-sm btn-outline-primary p-0 d-flex align-items-center justify-content-center btn-icon-28"
                              >
                                <i className="bi bi-eye-fill"></i>
                              </a>
                            )}
                            <button
                              title="Delete"
                              aria-label="Delete"
                              className="btn btn-sm btn-outline-danger p-0 d-flex align-items-center justify-content-center btn-icon-28"
                              onClick={() => handleDelete(row, index)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EmployeeEducationBackgroundTable;
